import {BlockType} from "./model/block";
import {Instruction} from "./model/instruction";
import {Program} from "./model/program";

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface SimpleEdge {
  id: string;
  nextNode: GridNode;
}

interface GridNode {
  rect: Rect;
  instruction: Instruction;
  rightEdge: SimpleEdge|null;
  downEdge: SimpleEdge|null;
  prevInstruction: Instruction|null;
}

export interface TreeChartNode {
  style: {left: string; top: string, width: string, height: string};
  rect: Rect;
  origX: number;
  origY: number;
  instruction: Instruction;
  prevNode: TreeChartNode|null;
  rightNode: TreeChartNode|null;
  downNode: TreeChartNode|null;
  traceInputs?: any;
  traceOutput?: any;
}

export interface TreeChartEdge {
  id: string;
  style: {left: string; top: string, width: string, height: string};
  cssClass: string;
  label?: string;
  labelStyle?: {left: string; top: string};
  node: TreeChartNode;
  nextNode: TreeChartNode;
  isRight: boolean; // false means isDown
}

export interface TreeChart {
  program: Program;
  nodes: TreeChartNode[];
  nodesByInstructionId: {[id: number]: TreeChartNode};
  edges: TreeChartEdge[];
  width: number;
  height: number;
}

const styleFromRect = (rect: Rect) => {
  const {x, y, width: w, height: h} = rect;
  return {left: x + 'px', top: y + 'px', width: w + 'px', height: h + 'px'};
};
const styleFromPoint = ({x, y}) => {
  return {left: x + 'px', top: y + 'px'};
};

// hv tree layout (hv is horizontal-vertical)
// Guarantees non-overlapping trees, but doesn't do any space compaction. It always lays out
// nodes below before laying out to the right.
const doLayoutSimple = (t: Instruction[], x: number, y: number, out: {[instructionId: string]: GridNode}, prevInstruction: Instruction|null): Rect => {
  if (t.length === 0) {
    return {x, y, width: 0, height: 0};
  }
  const cur: Instruction = t[0];

  const {rightTree, downTree} = cur.block.type === BlockType.Decision ?
    {rightTree: cur.yes, downTree: cur.no} : {rightTree: t.slice(1), downTree: []};
  const rectDown = doLayoutSimple(downTree, x, y + 1, out, cur);
  const rectRight = doLayoutSimple(rightTree, x + Math.max(1, rectDown.width), y, out, cur);

  const width: number = Math.max(1, rectDown.width) + rectRight.width;
  const height: number = Math.max(rectDown.height + 1, rectRight.height);

  // compute edges
  const rightEdge = rightTree.length ? {id: `r${cur.id}`, x1: x, y1: y, x2: rectRight.x, y2: rectRight.y, nextNode: out[rightTree[0].id]} : null;
  const downEdge = downTree.length ? {id: `d${cur.id}`, x1: x, y1: y, x2: rectDown.x, y2: rectDown.y, nextNode: out[downTree[0].id]} : null;

  const rect = {x, y, width, height};
  out[cur.id] = {rect, instruction: cur, rightEdge, downEdge, prevInstruction};
  return rect;
};

// hv tree layout (hv is horizontal-vertical)
// Guarantees non-overlapping trees, but doesn't do any space compaction. It always lays out
// nodes below before laying out to the right.
const doLayoutComplex = (t: Instruction[], isRight: boolean, x: number, y: number, out: {[instructionId: string]: GridNode}, firstFree, prevInstruction: Instruction|null) => {
  const cur: Instruction = t[0];

  const {rightTree, downTree} = cur.block.type === BlockType.Decision ?
    {rightTree: cur.yes, downTree: cur.no} : {rightTree: isRight ? t.slice(1) : [], downTree: !isRight ? t.slice(1) : []};

  // 1. Place self based on firstFree
  while (y >= firstFree.length) {
    firstFree.push(0);
  }
  x = Math.max(x, firstFree[y]);
  firstFree[y] += 1;

  // 2. Recurse down
  if (downTree.length) {
    doLayoutComplex(downTree, false, x, y + 1, out, firstFree, cur);
  }

  if (rightTree.length) {
    // 3. Calculate right-side depth
    const findDepth = (tt, goingRight) => {
      if (tt.length === 0) {
        return 0;
      }
      const i: Instruction = tt[0];
      // "no" path always goes down
      if (i.no && i.no.length > 0) {
        return findDepth(i.no, false) + 1;
      } else if (goingRight) {
        return 1;
      } else {
        // when going down, depth is calculated by following the instructions array
        return findDepth(tt.slice(1), false) + 1;
      }
    };
    const nodeHeight = findDepth(rightTree, true);

    while (y + nodeHeight >= firstFree.length) {
      firstFree.push(0);
    }

    let maxX = x;
    for (let i = 0; i < nodeHeight; i++) {
      maxX = Math.max(maxX, firstFree[y + i]);
    }
    for (let i = 0; i < nodeHeight; i++) {
      firstFree[y + i] = maxX;
    }

    // 4. Recurse right
    doLayoutComplex(rightTree, true, x + 1, y, out, firstFree, cur);
  }

  // compute edges
  const rightEdge = rightTree.length ? {id: `r${cur.id}`, nextNode: out[rightTree[0].id]} : null;
  const downEdge = downTree.length ? {id: `d${cur.id}`, nextNode: out[downTree[0].id]} : null;

  const rect = {x, y, width: 0, height: 0};
  out[cur.id] = {rect, instruction: cur, rightEdge, downEdge, prevInstruction};
};

const layoutInstructionsOnGrid = (instructions: Instruction[], x: number, y: number): GridNode[] => {
  const out = {};
  // doLayoutSimple(instructions, x, y, out, null);
  doLayoutComplex(instructions, true, x, y, out, [x], null);
  return Object.values(out);
};

export const layoutProgram = (program: Program, gridSize: number, blockSizes: Map<BlockType, {w: number, h: number}>): TreeChart => {
  const instructions = program.instructions;

  const nodes = [];
  const nodesByInstructionId = {};
  const edges = [];

  const layoutNodes = layoutInstructionsOnGrid(instructions, 0, 0);
  let maxX = 0;
  let maxY = 0;

  // first layout the nodes onto the grid
  layoutNodes.forEach((n: GridNode) => {
    // map nodes to the center of their grid cell
    const {rect: r, instruction} = n;
    const size = blockSizes.get(instruction.block.type) || {w: 50, h: 50};
    const nodeRect = {
      x: r.x * gridSize + gridSize / 2 - size.w / 2,
      y: r.y * gridSize + gridSize / 2 - size.h / 2,
      width: size.w,
      height: size.h,
    };
    const node = {
      style: styleFromRect(nodeRect),
      rect: nodeRect,
      origX: r.x,
      origY: r.y,
      instruction,
    };
    nodes.push(node);
    nodesByInstructionId[node.instruction.id] = node;

    // calculate max width / height as we go so we can set the size for the container div
    maxX = Math.max(maxX, (r.x + 1) * gridSize);
    maxY = Math.max(maxY, (r.y + 1) * gridSize);
  });

  // now link up prevNode, rightNode, downNode
  layoutNodes.forEach((n: GridNode) => {
    const node = nodesByInstructionId[n.instruction.id];
    const prevNode = n.prevInstruction ? nodesByInstructionId[n.prevInstruction.id] : null;
    const rightNode = n.rightEdge ? nodesByInstructionId[n.rightEdge.nextNode.instruction.id] : null;
    const downNode = n.downEdge ? nodesByInstructionId[n.downEdge.nextNode.instruction.id] : null;
    node.prevNode = prevNode;
    node.rightNode = rightNode;
    node.downNode = downNode;
  });

  const isAnchor = n =>
    !!n.downEdge ||
    n.instruction.block.type === BlockType.Start ||
    n.instruction.block.type === BlockType.End ||
    n.instruction.block.type === BlockType.Value;
  const startNode = layoutNodes.find(n => n.instruction.block.type === BlockType.Start);

  const evenlySpaceHorizontalNodes = (node, startAnchor = null) => {
    if (startAnchor === null && isAnchor(node)) {
      startAnchor = node;
    } else if (isAnchor(node)) {
      // now have startAnchor and endAnchor, if there is a gap beween them we can evenly space it
      if (node.rect.x - startAnchor.rect.x > 1) {
        const endAnchor = node;
        const rect1 = nodesByInstructionId[startAnchor.instruction.id].rect;
        const rect2 = nodesByInstructionId[endAnchor.instruction.id].rect;

        let numNodes = 0;
        let totalNodeSize = 0;
        let n = startAnchor.rightEdge.nextNode;
        while (n !== endAnchor) {
          totalNodeSize += nodesByInstructionId[n.instruction.id].rect.width;
          numNodes += 1;
          n = n.rightEdge.nextNode;
        }
        const numSpaces = numNodes + 1;
        const totalSpace = rect2.x - (rect1.x + rect1.width);
        const spaceSize = (totalSpace - totalNodeSize) / numSpaces;

        n = startAnchor.rightEdge.nextNode;
        let curX = rect1.x + rect1.width;
        while (n !== endAnchor) {
          const fullNode = nodesByInstructionId[n.instruction.id];
          curX += spaceSize;
          fullNode.rect.x = curX;
          fullNode.style = styleFromRect(fullNode.rect);
          curX += fullNode.rect.width;
          n = n.rightEdge.nextNode;
        }
      }
      startAnchor = node;
    }

    if (node.rightEdge) {
      evenlySpaceHorizontalNodes(node.rightEdge.nextNode, startAnchor);
    }
    if (node.downEdge) {
      evenlySpaceHorizontalNodes(node.downEdge.nextNode, null);
    }
  };
  evenlySpaceHorizontalNodes(startNode);

  // loop again to create edges now that we know the size of all blocks
  layoutNodes.forEach((n: GridNode) => {
    // map edges to their correct locations (and add yes/no label for decisions)
    const {instruction} = n;
    const isDecision = instruction.block.type === BlockType.Decision;
    const node = nodesByInstructionId[instruction.id];
    const nodeRect = node.rect;
    if (n.rightEdge) {
      const nextNode = nodesByInstructionId[n.rightEdge.nextNode.instruction.id];
      const nextNodeRect = nextNode.rect;
      const x = nodeRect.x + nodeRect.width;
      const y = nodeRect.y + nodeRect.height / 2 - 1;
      const w = nextNodeRect.x - x;
      const h = 2;
      const labelX = 3;
      const labelY = -20;
      edges.push({
        id: n.rightEdge.id,
        style: styleFromRect({x, y, width: w, height: h}),
        cssClass: "right",
        label: isDecision ? "yes" : null,
        labelStyle: isDecision ? styleFromPoint({x: labelX, y: labelY}) : null,
        dropZoneStyle: styleFromRect({x: 0, y: -15, width: w, height: 30}),
        node,
        nextNode,
        isRight: true,
      });
    }
    if (n.downEdge) {
      const nextNode = nodesByInstructionId[n.downEdge.nextNode.instruction.id];
      const nextNodeRect = nextNode.rect;
      const x = nodeRect.x + nodeRect.width / 2 - 1;
      const y = nodeRect.y + nodeRect.height;
      const w = 2;
      const h = nextNodeRect.y - y;
      const labelX = 8;
      const labelY = -1;
      edges.push({
        id: n.downEdge.id,
        style: styleFromRect({x, y, width: w, height: h}),
        cssClass: "down",
        label: isDecision ? "no" : null,
        labelStyle: isDecision ? styleFromPoint({x: labelX, y: labelY}) : null,
        dropZoneStyle: styleFromRect({x: -15, y: 0, width: 30, height: h}),
        node,
        nextNode,
        isRight: false,
      });
    }
  });
  return {program, nodes, nodesByInstructionId, edges, width: maxX, height: maxY};
};
